import React from "react";
import { Badge as MantineBadge, BadgeProps, useMantineTheme } from "@mantine/core";

interface CustomBadgeProps
  extends Omit<BadgeProps, "styles" | "onClick">,
    Pick<React.HTMLAttributes<HTMLDivElement>, "onClick"> {
  variant?:
    | "default"
    | "primary"
    | "outline"
    | "light"
    | "skills"
    | "tools"
    | "orange";
}

/**
 * Badge - Themed badge component (Diaspora Modern palette).
 * Usage: <Badge variant="primary">New</Badge>
 * Variants: default (ivory), primary (tech teal), outline (ivory border),
 *           light (dimmed neutral), skills (tech teal), tools (terracotta).
 */
export const Badge: React.FC<CustomBadgeProps> = ({ variant = "default", ...props }) => {
  const theme = useMantineTheme();
  const other = theme.other as Record<string, string>;

  const tealTint = "rgba(123, 164, 165,";
  const terracottaTint = "rgba(212, 85, 43,";
  const ivoryTint = "rgba(245, 238, 224,";

  const getStyles = () => {
    switch (variant) {
      case "primary":
        return {
          root: {
            backgroundColor: `${tealTint} 0.15)`,
            color: other.pillarTech,
            border: `1px solid ${tealTint} 0.3)`,
          },
        };
      case "outline":
        return {
          root: {
            backgroundColor: "transparent",
            color: other.textPrimary,
            border: `1px solid ${ivoryTint} 0.25)`,
          },
        };
      case "light":
        return {
          root: {
            backgroundColor: `${ivoryTint} 0.06)`,
            color: other.textDimmed,
            border: `1px solid ${ivoryTint} 0.15)`,
          },
        };
      case "skills":
        return {
          root: {
            backgroundColor: `${tealTint} 0.1)`,
            color: other.pillarTech,
            border: `1px solid ${tealTint} 0.25)`,
          },
        };
      case "tools":
      case "orange": // alias — same terracotta signature
        return {
          root: {
            backgroundColor: `${terracottaTint} 0.1)`,
            color: other.signatureTerracotta,
            border: `1px solid ${terracottaTint} 0.25)`,
          },
        };
      default:
        return {
          root: {
            backgroundColor: `${ivoryTint} 0.1)`,
            color: other.textPrimary,
            border: "none",
          },
        };
    }
  };

  return <MantineBadge {...props} variant="light" styles={getStyles()} />;
};

export default Badge;
